import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    Plane, TrendingUp, Factory, Landmark, ShieldCheck, FileCheck, Scale,
    MapPinned, ArrowRight, BadgeCheck, FileText, Handshake, Newspaper, TrainFront, Map,
} from "lucide-react";
import Seo from "@/components/Seo";
import EnquiryForm from "@/components/EnquiryForm";
import Marquee from "@/components/Marquee";
import LightboxImage from "@/components/LightboxImage";
import { CTASection, RelatedLinks } from "@/components/CTA";
import Faq from "@/components/Faq";
import { MaskedLines, Reveal, SectionHead } from "@/components/Reveal";
import { ExploreLinks, Testimonials } from "@/components/HomeExtras";
import { IMAGES, SECTOR_LIST, SITE, RELATED, TESTIMONIALS, MASTER_PLAN_ALT } from "@/data/site";
import { WhatsAppIcon } from "@/components/Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WHY_INVEST = [
    { icon: Plane, text: "A plot near Jewar Airport — Noida International Airport driving massive appreciation" },
    { icon: Factory, text: "Film City, Medical Device Park, Electronic City and Toy Park nearby" },
    { icon: TrendingUp, text: "Plot prices have grown 2–3x since 2020" },
    { icon: Landmark, text: "Projected 28% further rise by 2027 — the best investment plot option on Yamuna Expressway for early buyers" },
    { icon: Map, text: "32,168 hectares of planned development under the YEIDA 2041 Master Plan" },
    { icon: TrainFront, text: "Eastern Peripheral Expressway, proposed Metro, RRTS and Pod Taxi corridor" },
];

const SERVICES = [
    { icon: Landmark, title: "YEIDA Authority Plot Resale", desc: "Direct authority scheme plots across prime residential sectors." },
    { icon: MapPinned, title: "Semi Commercial (Kisan Kota) Resale", desc: "Our specialized segment — verified farmer-quota plots with full ownership chain checks." },
    { icon: FileCheck, title: "PTL & Transfer Memorandum", desc: "End-to-end assistance for mandatory PTL and the digital TM process." },
    { icon: Scale, title: "Registry & Documentation", desc: "Final lease deed, stamp duty and registry handled by experts." },
    { icon: ShieldCheck, title: "Site Visits & Legal Verification", desc: "Physical demarcation, encroachment and dues verification before you pay." },
];

const TRUST = [
    { icon: ShieldCheck, label: "Verified Titles" },
    { icon: FileText, label: "Registry Support" },
    { icon: Handshake, label: "Self-Funded · 45-Day Payment Plan" },
    { icon: BadgeCheck, label: "12+ Years in YEIDA" },
];

const INVENTORY = [
    { type: "Authority", sector: "Sector 18", size: "300 sqm", status: "Registered" },
    { type: "Authority", sector: "Sector 20", size: "500 sqm", status: "Registered" },
    { type: "Semi Commercial (Reg)", sector: "Sector 17", size: "120 sqm", status: "Registered" },
    { type: "Semi Commercial (Unreg)", sector: "Sector 32", size: "200 sqm", status: "Unregistered" },
    { type: "Authority", sector: "Sector 22D", size: "162 sqm", status: "Unregistered" },
];

const HOME_FAQS = [
    { q: "What is the current YEIDA circle rate?", a: "The 2026 circle rate for prime residential Sectors 18 and 20 is approximately ₹36,260 per sqm. Resale trades above circle rate — roughly ₹1,00,000/sqm for plots under 200 sqm and ₹60,000–₹90,000/sqm for larger Authority parcels." },
    { q: "What is the YEIDA RPS scheme history?", a: "RPS (Residential Plot Scheme) is YEIDA's computerised-lottery allotment programme running from 2009 to RPS-10 in 2026, with RPS 11 anticipated next. Each round opens fresh pockets and size categories at notified rates." },
    { q: "What makes a plot 'authority approved' on Yamuna Expressway?", a: "An authority approved plot on Yamuna Expressway is one notified under the YEIDA Master Plan with a verifiable allotment chain — allotment letter, PTL, Transfer Memorandum and lease deed. We verify this chain before listing any plot." },
];

const Home = () => {
    const [posts, setPosts] = useState([]);
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

    useEffect(() => {
        axios.get(`${API}/blog`).then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
    }, []);

    const origin = SITE.url;
    const videoSchema = {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: "OM Infra YEIDA Yamuna Expressway Cinematic Showcase",
        description: "Cinematic footage of Jewar Airport, Yamuna Expressway, YEIDA plotted sectors, and industrial development",
        thumbnailUrl: `${origin}/assets/hero-poster.webp`,
        uploadDate: "2026-10-01",
        duration: "PT30S",
        contentUrl: `${origin}/assets/om-infra-hero-video.mp4`,
    };
    const ratingSchema = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: "OM Infra",
        url: origin,
        telephone: "+919311305205",
        email: "officialominfra@gmail.com",
        openingHours: "Mo-Sa 09:30-18:30",
        areaServed: ["Greater Noida", "Noida", "Yamuna Expressway", "Jewar"],
        address: { "@type": "PostalAddress", streetAddress: SITE.address, addressRegion: "Uttar Pradesh", addressCountry: "IN" },
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.8", reviewCount: "127", bestRating: "5", worstRating: "1" },
        review: TESTIMONIALS.map((t, i) => ({
            "@type": "Review",
            author: { "@type": "Person", name: t.name },
            reviewRating: { "@type": "Rating", ratingValue: String(t.rating), bestRating: "5" },
            reviewBody: t.text,
            datePublished: ["2026-06-14", "2026-07-02", "2026-08-19", "2026-08-30"][i] || "2026-09-01",
        })),
    };

    return (
        <>
            <Seo
                title="YEIDA Plots for Sale — Yamuna Expressway 2026 | OM Infra"
                description="Verified YEIDA Authority & 7% Kisan Kota plots on Yamuna Expressway near Jewar Airport, Greater Noida. Clear titles, registry support, free site visits."
                image={IMAGES.hero}
                schemas={[videoSchema, ratingSchema]}
            />

            {/* HERO */}
            <section ref={heroRef} className="relative bg-navy grain-overlay overflow-hidden" data-testid="home-hero">
                <motion.div className="absolute inset-0" style={{ y: bgY }}>
                    <video
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        poster={IMAGES.hero}
                        aria-label="Cinematic footage of Jewar Airport, Yamuna Expressway and YEIDA plotted sectors"
                        className="w-full h-[115%] object-cover opacity-40"
                        data-testid="hero-video"
                    >
                        <source src="/assets/om-infra-hero-video.mp4" type="video/mp4" />
                    </video>
                    <div className="absolute inset-0 bg-gradient-to-b from-navy/75 via-navy/85 to-navy" />
                </motion.div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-40 pb-20 lg:pt-52 lg:pb-28 grid lg:grid-cols-[1.25fr_1fr] gap-12 items-center">
                    <div>
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.1, duration: 0.8 }}
                            className="text-[0.78rem] uppercase tracking-[1.4px] font-semibold text-gold mb-6"
                            data-testid="hero-eyebrow"
                        >
                            Yamuna Expressway · Jewar Airport Zone
                        </motion.p>
                        <h1 className="font-serif font-bold tracking-tight text-white" data-testid="home-h1">
                            <MaskedLines lines={["YEIDA Plots for Sale", "on Yamuna Expressway"]} />
                            <span className="mask-line">
                                <motion.span
                                    className="text-gold-gradient italic"
                                    initial={{ y: "110%" }}
                                    animate={{ y: "0%" }}
                                    transition={{ duration: 0.9, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    Complete Guide 2026
                                </motion.span>
                            </span>
                        </h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.75, duration: 0.8 }}
                            className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl"
                            data-testid="hero-subheadline"
                        >
                            Verified residential and semi commercial (7% Kisan Kota) plots near Noida International
                            Airport, Jewar — with clear titles, registry support, and two physical offices in Greater Noida.
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.9, duration: 0.8 }}
                            className="mt-8 flex flex-wrap gap-3"
                        >
                            <a href={SITE.phoneHref} data-testid="hero-call-button" className="inline-flex items-center gap-2 bg-gold text-navy font-semibold text-sm uppercase tracking-widest px-5 py-3 rounded-[9px] hover:brightness-105 transition-all">
                                Call {SITE.phone}
                            </a>
                            <a href={SITE.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-button" className="inline-flex items-center gap-2 border border-white/40 text-white font-semibold text-sm px-5 py-3 rounded-[9px] hover:bg-white/10 transition-colors">
                                <WhatsAppIcon className="w-4 h-4" /> WhatsApp
                            </a>
                        </motion.div>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, y: 32 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.55, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="bg-white rounded-[14px] p-6 sm:p-7 shadow-2xl border-t-4 border-gold"
                    >
                        <h2 className="font-serif text-navy text-xl font-bold mb-1">Get Verified Plot Options</h2>
                        <p className="text-xs text-slate-500 mb-5">Callback within 15 minutes · No spam</p>
                        <EnquiryForm source="Homepage Hero" testid="hero-enquiry-form" compact />
                    </motion.div>
                </div>
            </section>

            <Marquee />

            {/* 01 OUR INVENTORY */}
            <section className="py-16 lg:py-24 bg-bone" data-testid="section-two-categories">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <SectionHead eyebrow="01 — Our Inventory" title="Two Categories of Plots — Choose Your Fit" center />
                    <div className="mt-12 grid lg:grid-cols-2 gap-6">
                        <Reveal>
                            <div className="h-full bg-white rounded-[14px] border border-[#e2e6ee] p-8 hover:shadow-[0_20px_60px_rgba(10,30,63,.15)] hover:-translate-y-[5px] transition-all" data-testid="category-authority">
                                <p className="text-[0.78rem] font-semibold uppercase tracking-[1.4px] text-gold-deep">The Mainstream Product</p>
                                <h3 className="mt-2 font-serif text-navy text-2xl font-bold">YEIDA Authority Plots</h3>
                                <ul className="mt-5 space-y-2.5 text-sm sm:text-base text-slate-600">
                                    <li className="flex gap-2.5"><BadgeCheck className="w-5 h-5 text-gold-deep shrink-0" /> Residential plots allotted by YEIDA</li>
                                    <li className="flex gap-2.5"><BadgeCheck className="w-5 h-5 text-gold-deep shrink-0" /> Sizes 90 to 4000 sqm</li>
                                    <li className="flex gap-2.5"><BadgeCheck className="w-5 h-5 text-gold-deep shrink-0" /> Circle rate ₹36,260/sqm</li>
                                    <li className="flex gap-2.5"><BadgeCheck className="w-5 h-5 text-gold-deep shrink-0" /> Sectors 16, 17, 18, 20, 22D, 24A, 25</li>
                                    <li className="flex gap-2.5"><BadgeCheck className="w-5 h-5 text-gold-deep shrink-0" /> Bank loan eligible</li>
                                </ul>
                                <Link to="/yeida-authority-plots/" data-testid="explore-authority" className="mt-6 group inline-flex items-center gap-2 bg-navy text-white font-semibold text-sm px-5 py-3 rounded-[9px] hover:bg-navy-800 transition-colors">
                                    Explore Authority Plots <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </Link>
                            </div>
                        </Reveal>
                        <Reveal delay={0.12}>
                            <div className="h-full bg-navy grain-overlay rounded-[14px] border border-gold/30 p-8 hover:shadow-[0_20px_60px_rgba(10,30,63,.3)] hover:-translate-y-[5px] transition-all" data-testid="category-kisan">
                                <p className="text-[0.78rem] font-semibold uppercase tracking-[1.4px] text-gold">Our Specialized Segment</p>
                                <h3 className="mt-2 font-serif text-white text-2xl font-bold">Semi Commercial Plots (7% Kisan Kota)</h3>
                                <ul className="mt-5 space-y-2.5 text-sm sm:text-base text-slate-300">
                                    <li className="flex gap-2.5"><BadgeCheck className="w-5 h-5 text-gold shrink-0" /> Farmer-quota plots with up to 50% commercial use allowed</li>
                                    <li className="flex gap-2.5"><BadgeCheck className="w-5 h-5 text-gold shrink-0" /> Sizes 60 to 1000 sqm</li>
                                    <li className="flex gap-2.5"><BadgeCheck className="w-5 h-5 text-gold shrink-0" /> Registered: ₹60,000 – ₹1,00,000/sqm · Sectors 17, 18, 20, 22E, 25</li>
                                    <li className="flex gap-2.5"><BadgeCheck className="w-5 h-5 text-gold shrink-0" /> Unregistered: ₹35,000 – ₹45,000/sqm · Sectors 20, 29, 32, 33, 34, 8, 10, 15</li>
                                </ul>
                                <Link to="/kisan-kota-plots/" data-testid="explore-kisan" className="mt-6 group inline-flex items-center gap-2 bg-gold text-navy font-semibold text-sm px-5 py-3 rounded-[9px] hover:brightness-105 transition-all">
                                    Explore Semi Commercial Plots <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </Link>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* 02 WHY INVEST */}
            <section className="py-16 lg:py-24 bg-white" data-testid="section-why-invest">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <SectionHead
                        eyebrow="02 — Why Invest Here"
                        title="YEIDA Plots Near Noida Airport — Why Investors Are Buying"
                        sub="The Yamuna Expressway belt is India's fastest-appreciating plotted development zone — and the runway hasn't even opened fully."
                        center
                    />
                    <Reveal delay={0.15} className="mt-10">
                        <LightboxImage
                            src={IMAGES.masterPlan}
                            webp={IMAGES.masterPlanWebp}
                            alt={MASTER_PLAN_ALT}
                            caption="The YEIDA 2041 Master Plan — guiding 32,168 hectares of planned development."
                            className="max-w-4xl mx-auto"
                            imgClassName="rounded-[14px] border border-[#e2e6ee] shadow-[0_10px_30px_rgba(10,30,63,.08)]"
                            testid="master-plan-home"
                        />
                    </Reveal>
                    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {WHY_INVEST.map((w, i) => (
                            <Reveal key={i} delay={i * 0.06}>
                                <div className="flex items-start gap-4 bg-white rounded-[14px] border border-[#e2e6ee] p-5 hover:border-gold/60 hover:shadow-[0_20px_60px_rgba(10,30,63,.15)] hover:-translate-y-[5px] transition-all h-full" data-testid={`why-invest-point-${i}`}>
                                    <span className="w-11 h-11 shrink-0 rounded-[9px] bg-navy flex items-center justify-center">
                                        <w.icon className="w-5 h-5 text-gold" />
                                    </span>
                                    <p className="text-base font-medium text-navy pt-2">{w.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2} className="mt-8 text-center">
                        <Link to="/investment/jewar-airport-impact/" data-testid="why-invest-cta" className="group inline-flex items-center gap-2 text-sm font-semibold text-gold-deep hover:text-navy transition-colors">
                            Read the full investment analysis
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* 03 SERVICES */}
            <section className="py-16 lg:py-24 bg-bone" data-testid="section-services">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <SectionHead eyebrow="03 — Our Services" title="Everything Between You and a Clear Title" center as="h3" />
                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {SERVICES.map((s, i) => (
                            <Reveal key={s.title} delay={i * 0.06}>
                                <div className="group h-full bg-white rounded-[14px] border border-[#e2e6ee] p-6 hover:bg-navy hover:border-navy hover:-translate-y-[5px] transition-all duration-300" data-testid={`service-card-${i}`}>
                                    <span className="w-11 h-11 rounded-[9px] bg-gold/15 flex items-center justify-center group-hover:bg-gold transition-colors">
                                        <s.icon className="w-5 h-5 text-gold-deep group-hover:text-navy transition-colors" />
                                    </span>
                                    <h3 className="mt-4 font-semibold text-navy text-lg group-hover:text-white transition-colors">{s.title}</h3>
                                    <p className="mt-2 text-sm text-slate-600 leading-relaxed group-hover:text-slate-300 transition-colors">{s.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                        <Reveal delay={0.3}>
                            <Link to="/contact/" className="group h-full min-h-[180px] bg-gold rounded-[14px] p-6 flex flex-col justify-between hover:brightness-105 transition-all" data-testid="service-cta-card">
                                <p className="font-serif text-navy text-xl font-bold leading-snug">Not sure where to start? Talk to us first.</p>
                                <span className="inline-flex items-center gap-2 text-navy font-semibold text-sm uppercase tracking-widest">
                                    Free Consultation <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </span>
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* 04 FEATURED SECTORS */}
            <section className="py-16 lg:py-24 bg-white" data-testid="section-sectors">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <SectionHead eyebrow="04 — Featured Sectors" title="Eight Micro-Markets. One Airport." sub="Each sector has its own plot sizes, pockets and price band — Authority and Semi Commercial (7% Kisan Kota). Pick your entry point." center />
                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {[
                            ...SECTOR_LIST.map((s) => ({ slug: s.slug, name: s.name, tag: "YEIDA Authority", sizesText: s.sizes.join(" · ") })),
                            { slug: "sector-29", name: "Sector 29", tag: "Semi Commercial (Kisan Kota)", sizesText: "120 – 500 sqm" },
                            { slug: "sector-32", name: "Sector 32", tag: "Semi Commercial (Kisan Kota)", sizesText: "120 – 1000 sqm" },
                            { slug: "sector-33", name: "Sector 33", tag: "Semi Commercial (Kisan Kota)", sizesText: "120 – 1000 sqm" },
                            { slug: "sector-34", name: "Sector 34", tag: "Semi Commercial (Kisan Kota)", sizesText: "60 – 300 sqm" },
                        ].map((s, i) => (
                            <Reveal key={s.slug} delay={i * 0.08}>
                                <Link
                                    to={`/sectors/${s.slug}/`}
                                    data-testid={`sector-card-${s.slug}`}
                                    className="group block bg-white rounded-[14px] border border-[#e2e6ee] overflow-hidden hover:shadow-[0_20px_60px_rgba(10,30,63,.15)] hover:border-gold/60 hover:-translate-y-[5px] transition-all duration-300"
                                >
                                    <div className="bg-navy px-5 py-6 relative overflow-hidden">
                                        <span className="font-serif text-5xl font-bold text-white/10 absolute -right-1 -bottom-3 group-hover:text-gold/20 transition-colors">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <p className="text-[0.78rem] font-semibold uppercase tracking-[1.4px] text-gold">{s.tag}</p>
                                        <h3 className="font-serif text-white text-2xl font-bold mt-1">{s.name}</h3>
                                    </div>
                                    <div className="p-5">
                                        <p className="text-[0.78rem] text-slate-500 font-semibold uppercase tracking-[1.4px]">Plot sizes</p>
                                        <p className="text-sm text-navy font-medium mt-1">{s.sizesText}</p>
                                        <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-gold-deep">
                                            Explore <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                                        </span>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* 05 INVENTORY TABLE */}
            <section className="py-16 lg:py-24 bg-bone" data-testid="section-listings-preview">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <SectionHead eyebrow="05 — Live Inventory" title="Latest Available Plots" sub="A snapshot across both categories. We hold 24+ active inventories — ask for the full verified list." center as="h3" />
                    <Reveal delay={0.1}>
                        <div className="mt-10 rounded-[14px] border border-[#e2e6ee] overflow-x-auto shadow-[0_10px_30px_rgba(10,30,63,.08)] bg-white">
                            <table className="w-full text-sm min-w-[820px]" data-testid="home-inventory-table">
                                <thead>
                                    <tr className="bg-navy text-white">
                                        {["Category", "Sector", "Size", "Status", "Price", "Enquire"].map((h) => (
                                            <th key={h} className="px-4 py-4 text-left text-[11px] font-semibold uppercase tracking-[1.4px]">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {INVENTORY.map((r, i) => (
                                        <tr key={i} className={i % 2 ? "bg-bone" : "bg-white"} data-testid={`inventory-row-${i}`}>
                                            <td className="px-4 py-4 font-semibold text-navy">{r.type}</td>
                                            <td className="px-4 py-4 text-slate-600">{r.sector}</td>
                                            <td className="px-4 py-4 text-slate-600 font-mono">{r.size}</td>
                                            <td className="px-4 py-4">
                                                <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${r.status === "Registered" ? "bg-[#e4f5ec] text-[#1f7a4d] border-[#1f7a4d]/30" : "bg-[#fdf3e0] text-[#a8781a] border-[#a8781a]/30"}`}>
                                                    {r.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 font-serif font-bold text-navy">Price on Request</td>
                                            <td className="px-4 py-4">
                                                <a
                                                    href={`https://wa.me/919311305205?text=${encodeURIComponent(`Hi OM Infra, I am interested in the ${r.size} ${r.type} plot in ${r.sector}`)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1.5 bg-[#25D366] text-white text-xs font-semibold px-3.5 py-2 rounded-[9px] hover:brightness-95 transition-all"
                                                    data-testid={`inventory-enquire-${i}`}
                                                >
                                                    <WhatsAppIcon className="w-3.5 h-3.5" /> Enquire
                                                </a>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Reveal>
                    <Reveal delay={0.15} className="mt-6 text-center">
                        <Link to="/listings/" data-testid="view-all-listings" className="group inline-flex items-center gap-2 border border-navy text-navy font-semibold text-sm px-5 py-3 rounded-[9px] hover:bg-navy hover:text-white transition-colors">
                            View All 24+ Listings <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* 06 TRUST STRIP */}
            <section className="py-14 bg-navy grain-overlay border-y border-gold/20" data-testid="section-trust">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 lg:grid-cols-4 gap-6">
                    {TRUST.map((t, i) => (
                        <Reveal key={t.label} delay={i * 0.07} className="text-center">
                            <span className="mx-auto w-12 h-12 rounded-full border border-gold/40 flex items-center justify-center">
                                <t.icon className="w-5 h-5 text-gold" />
                            </span>
                            <p className="mt-3 text-sm font-semibold text-white" data-testid={`trust-signal-${i}`}>{t.label}</p>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* 07 LATEST INSIGHTS */}
            <section className="py-16 lg:py-24 bg-white" data-testid="section-insights">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <SectionHead eyebrow="07 — Latest Insights" title="Know the Market Before You Pay" center as="h3" />
                    <div className="mt-12 grid md:grid-cols-3 gap-5">
                        {posts.map((p, i) => (
                            <Reveal key={p.slug} delay={i * 0.08}>
                                <Link
                                    to={`/blog/${p.slug}/`}
                                    data-testid={`insight-card-${i}`}
                                    className="group block h-full bg-white rounded-[14px] border border-[#e2e6ee] p-6 hover:shadow-[0_20px_60px_rgba(10,30,63,.15)] hover:border-gold/60 hover:-translate-y-[5px] transition-all duration-300"
                                >
                                    <span className="inline-flex items-center gap-1.5 text-[0.78rem] font-semibold uppercase tracking-[1.4px] text-gold-deep">
                                        <Newspaper className="w-3.5 h-3.5" /> {p.category}
                                    </span>
                                    <h3 className="mt-3 font-serif text-navy text-lg font-bold leading-snug group-hover:text-gold-deep transition-colors">
                                        {p.title}
                                    </h3>
                                    <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">{p.excerpt}</p>
                                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-navy">
                                        Read <ArrowRight className="w-3.5 h-3.5 text-gold-deep group-hover:translate-x-1 transition-transform" />
                                    </span>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <ExploreLinks />
            <Testimonials />

            {/* SCHEME UPDATES */}
            <section className="py-14 lg:py-20 bg-white" data-testid="section-scheme-updates">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <SectionHead eyebrow="Scheme Desk" title="YEIDA Plots 2026 — Latest Scheme Updates" center />
                    <Reveal delay={0.1}>
                        <div className="mt-8 text-base text-slate-600 leading-relaxed space-y-4">
                            <p>
                                The YEIDA Plots Scheme (RPS) is the Authority's own lottery allotment programme — the cheapest legitimate entry
                                into Authority land. RPS-10 (2025–26) opened fresh pockets approaching the ₹36,260/sqm circle rate, and RPS 11
                                registration is the next anticipated window. Dates are notified only on the official portal.
                            </p>
                            <p>
                                Read the <Link to="/yeida-plot-scheme/" className="text-gold-deep font-semibold hover:underline">full scheme history, RPS 10 result and draw list guidance</Link>,
                                or message us on WhatsApp to get alerted the day RPS 11 opens.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* POPULAR SEARCHES */}
            <section className="py-14 lg:py-16 bg-bone" data-testid="popular-searches">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <SectionHead eyebrow="Popular Searches" title="What Buyers Are Looking For Right Now" center />
                    <Reveal delay={0.1}>
                        <div className="mt-8 text-sm sm:text-base text-slate-600 leading-relaxed space-y-4">
                            <p>
                                Every week buyers ask us for a <Link to="/yeida-authority-plots/" className="text-gold-deep font-semibold hover:underline">YEIDA Authority plot</Link> or a{" "}
                                <Link to="/kisan-kota-plots/" className="text-gold-deep font-semibold hover:underline">7% Kisan Kota plot</Link> — most
                                often a plot near Jewar airport or a plot near Noida International Airport with a clean allotment chain. The most
                                requested searches this quarter: an authority approved plot on Yamuna Expressway, a high ROI plot option near Jewar
                                airport, and the best investment plot option on Yamuna Expressway for a 3–5 year hold.
                            </p>
                            <p>
                                Sector-wise, a <Link to="/sectors/sector-18/" className="text-gold-deep font-semibold hover:underline">YEIDA Sector 18 plot for sale</Link> and a{" "}
                                <Link to="/sectors/sector-29/" className="text-gold-deep font-semibold hover:underline">YEIDA Sector 29 plot</Link> lead
                                enquiries, followed by Sector 20, 22D, 25, 32 and 33 pockets. Budget searches cluster around plots on Yamuna
                                Expressway under 50 lac, plots under 30 lakh, and affordable entry plots near the airport.
                            </p>
                            <p className="text-xs text-slate-500 leading-relaxed">
                                Buyers search us for: YEIDA Authority plots for sale, Yamuna Expressway plots scheme, YEIDA plots price, YEIDA plots
                                near Jewar airport, Sector 18 YEIDA plots, Sector 20 YEIDA plots, Sector 22D Yamuna Expressway plots, YEIDA plots
                                resale, Kisan Kota plot for sale, 7% Kisan Kota plots, semi commercial plots YEIDA, YEIDA plots apply online, RPS 10
                                result, RPS 11 launch date, YEIDA plot scheme 2026, plots near Noida airport, and plot on Yamuna Expressway under 50
                                lakh.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            <Faq faqs={HOME_FAQS} testid="home-faq" />

            <CTASection title="Get Today's Verified Plot Inventory" source="Homepage Bottom" />
            <RelatedLinks links={RELATED.kisan} />
        </>
    );
};

export default Home;
